import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ─────────────────────────────────────────────────────────────────────────────
// Diálogo accesible compartido (antes vivía solo en pages/admin/AdminUI.jsx).
// `useDialog` da a cualquier modal propio: foco al abrir ([data-autofocus] o el primer
// control), Tab/Shift+Tab atrapados, Escape cierra, bloqueo de scroll y foco de vuelta
// al disparador al cerrar. `Dialog` es el contenedor completo con role/aria-modal/título.
// ─────────────────────────────────────────────────────────────────────────────

export const FOCUSABLES = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Pila de diálogos abiertos: solo el de arriba responde a Escape/Tab (p. ej. una
// confirmación abierta encima de otro modal).
const pila = [];

/**
 * Comportamiento de teclado/foco de un modal.
 * @param {React.RefObject<HTMLElement>} ref  panel del diálogo (el que lleva role="dialog")
 * @param {boolean} abierto
 * @param {() => void} onCerrar  se llama con Escape
 */
export function useDialog(ref, abierto, onCerrar) {
  const cerrarRef = useRef(onCerrar);
  useEffect(() => { cerrarRef.current = onCerrar; }, [onCerrar]);

  useEffect(() => {
    if (!abierto) return undefined;
    const token = {};
    pila.push(token);
    const previo = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const t = setTimeout(() => {
      const p = ref.current;
      if (!p || p.contains(document.activeElement)) return;
      const el = p.querySelector('[data-autofocus]') || p.querySelector(FOCUSABLES) || p;
      el.focus();
    }, 30);

    const teclas = (e) => {
      if (pila[pila.length - 1] !== token) return;
      const p = ref.current;
      if (!p) return;
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        cerrarRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = [...p.querySelectorAll(FOCUSABLES)].filter(x => x.offsetParent !== null);
      if (!items.length) { e.preventDefault(); p.focus(); return; }
      const primero = items[0];
      const ultimo = items[items.length - 1];
      const activo = document.activeElement;
      if (!p.contains(activo)) { e.preventDefault(); (e.shiftKey ? ultimo : primero).focus(); }
      else if (e.shiftKey && (activo === primero || activo === p)) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && activo === ultimo) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener('keydown', teclas, true);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', teclas, true);
      const i = pila.indexOf(token);
      if (i >= 0) pila.splice(i, 1);
      document.body.style.overflow = overflow;
      if (previo && previo !== document.body && typeof previo.focus === 'function' && document.contains(previo)) previo.focus();
    };
  }, [abierto, ref]);
}

export function Dialog({ abierto, onCerrar, titulo, descripcion, children, pie, variante = 'modal', alerta = false, ancho = 'max-w-lg' }) {
  const panel = useRef(null);
  const [visto, setVisto] = useState(false);
  const idTitulo = useId();
  const idDesc = useId();

  useDialog(panel, abierto, onCerrar);

  useEffect(() => {
    if (!abierto) { setVisto(false); return undefined; }
    const raf = requestAnimationFrame(() => setVisto(true));
    return () => cancelAnimationFrame(raf);
  }, [abierto]);

  if (!abierto) return null;

  const esDrawer = variante === 'drawer';
  return createPortal(
    <div className="fixed inset-0 z-[100000]">
      <div className={`absolute inset-0 bg-slate-900/60 transition-opacity duration-200 motion-reduce:transition-none ${visto ? 'opacity-100' : 'opacity-0'}`} onClick={onCerrar} aria-hidden="true" />
      <div className={`absolute inset-0 flex ${esDrawer ? 'items-end sm:items-stretch justify-end' : 'items-end sm:items-center justify-center p-0 sm:p-4'} pointer-events-none`}>
        <div
          ref={panel} tabIndex={-1}
          role={alerta ? 'alertdialog' : 'dialog'} aria-modal="true" aria-labelledby={idTitulo} aria-describedby={descripcion ? idDesc : undefined}
          className={`pointer-events-auto flex flex-col w-full bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-2xl outline-none
            transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none
            ${esDrawer ? `sm:max-w-md max-h-[92vh] sm:max-h-none sm:h-full rounded-t-2xl sm:rounded-none ${visto ? 'translate-y-0 sm:translate-x-0 opacity-100' : 'translate-y-4 sm:translate-y-0 sm:translate-x-6 opacity-0'}`
              : `${ancho} max-h-[92vh] rounded-t-2xl sm:rounded-2xl ${visto ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}`}
        >
          <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
            <div className="min-w-0">
              <h2 id={idTitulo} className="text-lg font-semibold leading-tight text-balance">{titulo}</h2>
              {descripcion && <p id={idDesc} className="mt-1 text-sm text-slate-600 dark:text-slate-300">{descripcion}</p>}
            </div>
            <button type="button" onClick={onCerrar} aria-label="Cerrar"
              className="-mr-2 -mt-1 grid place-items-center w-11 h-11 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          {children && <div className="px-5 pb-5 overflow-y-auto flex-1">{children}</div>}
          {pie && <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">{pie}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
